import { fetchPost } from '../../../../../../helpers/fetching';

const getUniquePositions = (lesionData) => {
  if (!lesionData) return [];

  if (Array.isArray(lesionData) && typeof lesionData[0] === 'string') {
    return [lesionData.slice(0, 3)];
  }

  if (lesionData.unique_position) {
    return [lesionData.unique_position.slice(0, 3)];
  }

  if (Array.isArray(lesionData.matched_lesions)) {
    return lesionData.matched_lesions.map((item) => item.unique_position?.slice(0, 3)).filter(Boolean);
  }

  if (Array.isArray(lesionData.nested_items)) {
    return lesionData.nested_items.map((item) => item.unique_position?.slice(0, 3)).filter(Boolean);
  }

  return [];
};

const getSingleUniquePosition = (lesionData) => {
  const uniquePositions = lesionData?.[0]?.unique_position || lesionData;
  return getUniquePositions(uniquePositions)[0] || null;
};

// Table Actions - API Integration

const dispatchError = (msg, dispatch, actions, t) => {
  dispatch(
    actions.setSnackbar({
      msg: `${t('diagnose.general_error')} ${msg && msg}`,
      severity: 'error',
    })
  );
};

export const dispatchCustomLesionModulesUpdate = (sourceModuleType, data, dispatch, actions) => {
  const applyMicrocalc = () => {
    dispatch(actions.setCustomMicrocalc(data.customMicrocalc));
    dispatch(actions.setMicrocalcLocalChanges(true));
  };
  const applyOpacities = () => {
    dispatch(actions.setCustomOpacities(data.customOpacities));
    dispatch(actions.setOpacitiesLocalChanges(true));
  };

  if (sourceModuleType === 'microcalc') {
    applyMicrocalc();
    applyOpacities();
  } else {
    applyOpacities();
    applyMicrocalc();
  }
};

const removeFindingFromCustomData = (customData, findingInfo) => {
  if (!Array.isArray(findingInfo) || findingInfo.length < 3) {
    return { updatedData: customData, removed: false };
  }

  const [projection, findingClass, findingIndex] = findingInfo;
  const index = Number(findingIndex);

  if (!projection || !findingClass || !Number.isInteger(index)) {
    return { updatedData: customData, removed: false };
  }

  const currentProjectionData = customData?.[projection];
  const classFindings = currentProjectionData?.[findingClass];

  if (!Array.isArray(classFindings) || index < 0 || index >= classFindings.length) {
    return { updatedData: customData, removed: false };
  }

  const updatedClassFindings = classFindings.filter((_, i) => i !== index);
  const updatedProjectionData = {
    ...currentProjectionData,
    [findingClass]: updatedClassFindings,
  };

  return {
    updatedData: {
      ...customData,
      [projection]: updatedProjectionData,
    },
    removed: true,
  };
};

const MATCHED_PROJECTION_MAP = {
  lcc: 'lmlo',
  lmlo: 'lcc',
  rcc: 'rmlo',
  rmlo: 'rcc',
};

const BREAST_TO_PROJECTIONS = {
  right: ['rcc', 'rmlo'],
  left: ['lcc', 'lmlo'],
};

const removeFindingsByUniquePositions = (customData, uniquePositions) => {
  if (!Array.isArray(uniquePositions) || uniquePositions.length === 0) {
    return { updatedData: customData, removed: false };
  }

  const groupedIndexes = uniquePositions.reduce((acc, findingInfo) => {
    if (!Array.isArray(findingInfo) || findingInfo.length < 3) {
      return acc;
    }
    const [projection, findingClass, findingIndex] = findingInfo;
    const index = Number(findingIndex);
    if (!projection || !findingClass || !Number.isInteger(index)) {
      return acc;
    }

    const key = `${projection}::${findingClass}`;
    if (!acc[key]) {
      acc[key] = { projection, findingClass, indexes: new Set() };
    }
    acc[key].indexes.add(index);
    return acc;
  }, {});

  let removed = false;
  let updatedData = customData;

  Object.values(groupedIndexes).forEach(({ projection, findingClass, indexes }) => {
    const currentProjectionData = updatedData?.[projection];
    const classFindings = currentProjectionData?.[findingClass];
    if (!Array.isArray(classFindings)) {
      return;
    }

    const updatedClassFindings = classFindings.filter((_, idx) => !indexes.has(idx));
    if (updatedClassFindings.length !== classFindings.length) {
      removed = true;
      updatedData = {
        ...updatedData,
        [projection]: {
          ...currentProjectionData,
          [findingClass]: updatedClassFindings,
        },
      };
    }
  });

  return { updatedData, removed };
};

const removeMatchedFindingsFromCustomData = (customData, lesionData) => {
  const matchedUniquePositions = Array.isArray(lesionData?.matched_lesions)
    ? lesionData.matched_lesions.map((item) => item?.unique_position).filter(Boolean)
    : [];

  if (matchedUniquePositions.length > 0) {
    return removeFindingsByUniquePositions(customData, matchedUniquePositions);
  }

  const findingInfo = lesionData?.matched || lesionData?.unique_position;
  const baseRemoval = removeFindingFromCustomData(customData, findingInfo);
  if (!baseRemoval.removed) {
    return baseRemoval;
  }

  const [projection, findingClass, findingIndex] = findingInfo || [];
  const selectedFinding = customData?.[projection]?.[findingClass]?.[Number(findingIndex)];
  const lesionId = selectedFinding?.object_id;
  const matchedProjection = MATCHED_PROJECTION_MAP[projection];

  if (!lesionId || !matchedProjection || !baseRemoval.updatedData?.[matchedProjection]?.[findingClass]) {
    return baseRemoval;
  }

  const matchedClassFindings = baseRemoval.updatedData[matchedProjection][findingClass];
  const matchedIndex = matchedClassFindings.findIndex((item) => item?.object_id === lesionId);

  if (matchedIndex === -1) {
    return baseRemoval;
  }

  const updatedMatchedClassFindings = matchedClassFindings.filter((_, i) => i !== matchedIndex);

  return {
    updatedData: {
      ...baseRemoval.updatedData,
      [matchedProjection]: {
        ...baseRemoval.updatedData[matchedProjection],
        [findingClass]: updatedMatchedClassFindings,
      },
    },
    removed: true,
  };
};

const removeClassByBreastFromCustomData = (customData, breast, findingClass) => {
  const projections = BREAST_TO_PROJECTIONS[breast];

  if (!Array.isArray(projections) || !findingClass) {
    return { updatedData: customData, removed: false };
  }

  let removed = false;
  let updatedData = customData;

  projections.forEach((projection) => {
    const projectionData = updatedData?.[projection];
    const classFindings = projectionData?.[findingClass];
    if (!Array.isArray(classFindings)) {
      return;
    }

    if (classFindings.length > 0) {
      removed = true;
    }

    updatedData = {
      ...updatedData,
      [projection]: {
        ...projectionData,
        [findingClass]: [],
      },
    };
  });

  return { updatedData, removed };
};

// Delete all findings new integration with backend
export const deleteAllFindings = async (
  actionGroup,
  studyId,
  moduleType,
  lesionData,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t,
  e
) => {
  try {
    e?.stopPropagation();

    if (!lesionData) {
      console.error('Invalid lesionData structure:', lesionData);
      dispatchError(t('diagnose.general_error'), dispatch, actions, t);
      return;
    }

    dispatch(actions.setSavingPending(true));

    let sourceData = null;
    let setDataAction = null;
    let setLocalChangesAction = null;

    if (moduleType === 'opacities') {
      sourceData = customOpacities;
      setDataAction = actions.setCustomOpacities;
      setLocalChangesAction = actions.setOpacitiesLocalChanges;
    } else if (moduleType === 'microcalc') {
      sourceData = customMicrocalc;
      setDataAction = actions.setCustomMicrocalc;
      setLocalChangesAction = actions.setMicrocalcLocalChanges;
    } else {
      return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
    }

    let result = { updatedData: sourceData, removed: false };

    if (actionGroup === 'delete_matched') {
      result = removeMatchedFindingsFromCustomData(sourceData, lesionData);
    } else if (actionGroup === 'delete_class') {
      result = removeClassByBreastFromCustomData(sourceData, lesionData?.breast, lesionData?.finding_class);
    } else {
      return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
    }

    if (!result.removed) {
      return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
    }

    dispatch(setDataAction(result.updatedData));
    dispatch(setLocalChangesAction(true));

    dispatch(
      actions.setSnackbar({
        msg:
          actionGroup === 'delete_matched'
            ? t('diagnose.deleted_matched_findings')
            : t('diagnose.deleted_class'),
        severity: 'success',
      })
    );
  } catch (error) {
    console.error('Error deleting findings:', error);
    dispatchError(t('diagnose.general_error'), dispatch, actions, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

// Delete single finding new integration with backend
export const deleteFinding = async (
  studyId,
  moduleType,
  findingInfo,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t,
  e
) => {
  try {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    dispatch(actions.setSavingPending(true));

    dispatch(actions.resetHoverState());

    if (moduleType === 'microcalc') {
      const { updatedData, removed } = removeFindingFromCustomData(customMicrocalc, findingInfo);
      if (!removed) {
        return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
      }
      dispatch(actions.setMicrocalcLocalChanges(true));
      dispatch(actions.setCustomMicrocalc(updatedData));
    } else if (moduleType === 'opacities') {
      const { updatedData, removed } = removeFindingFromCustomData(customOpacities, findingInfo);
      if (!removed) {
        return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
      }
      dispatch(actions.setOpacitiesLocalChanges(true));
      dispatch(actions.setCustomOpacities(updatedData));
    } else {
      return dispatchError(t('diagnose.general_error'), dispatch, actions, t);
    }

    dispatch(
      actions.setSnackbar({
        msg: 'diagnose.deleted_finding',
        severity: 'success',
      })
    );
  } catch (error) {
    console.error('Error deleting finding:', error);
    dispatchError(t('diagnose.general_error'), dispatch, actions, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

// Unmatching logic new integration with backend and new structure
export const handleUnmatch = async (
  studyId,
  findingArray,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));

    const uniquePosition = getSingleUniquePosition(findingArray);
    const unmatchRequestBody = {
      module: 'opacities',
      action: 'unmatch',
      lesion: uniquePosition,
      study_id: studyId,
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
    };

    const response = await fetchPost('diagnostic_revision_step', unmatchRequestBody, 'bbox');

    if (response.success) {
      dispatch(actions.setCustomOpacities(response.data.customOpacities));
      dispatch(actions.setOpacitiesLocalChanges(true));
      dispatch(actions.updateJustEditedRows(response?.updatedFindingID));
      dispatch(actions.setSnackbar({ msg: 'diagnose.unmatched_findings', severity: 'success' }));
    } else {
      dispatchError(response?.msg, dispatch, actions, t);
    }
  } catch (error) {
    console.error('Error in handleUnmatch:', error);
    dispatchError(error?.message, dispatch, actions, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

// Class change handled with new integration with backend
export const handleClassChange = async (
  studyId,
  moduleType,
  box,
  newClass,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));

    const uniquePosition = getSingleUniquePosition(box);

    const requestBody = {
      module: moduleType,
      action: 'class_type_change',
      lesion: uniquePosition,
      new_class: newClass,
      study_id: studyId,
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
    };
    const response = await fetchPost('diagnostic_revision_step', requestBody, 'bbox');

    if (!response?.success) {
      return dispatchError(response?.msg, dispatch, actions, t);
    }

    const { data } = response;

    const moduleHandlers = {
      opacities: {
        key: 'customOpacities',
        setData: actions.setCustomOpacities,
        setLocal: actions.setOpacitiesLocalChanges,
      },
      microcalc: {
        key: 'customMicrocalc',
        setData: actions.setCustomMicrocalc,
        setLocal: actions.setMicrocalcLocalChanges,
      },
    };

    const handler = moduleHandlers[moduleType];
    if (!handler) {
      console.error(`Unsupported moduleType: ${moduleType}`);
      return dispatchError(response?.msg, dispatch, actions, t);
    }

    if (data?.[handler.key]) {
      dispatch(handler.setData(data[handler.key]));
      dispatch(handler.setLocal(true));
      dispatch(actions.updateJustEditedRows(response?.updatedFindingID));

      dispatch(
        actions.setSnackbar({
          msg: 'diagnose.class_changed',
          severity: 'success',
        })
      );
    } else {
      dispatchError(response?.msg, dispatch, actions, t);
    }
  } catch (error) {
    console.error('Error in handleClassChange:', error);
    dispatchError(error?.message, dispatch, actions, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

//Match findings
export const handleMatch = async (
  studyId,
  sourceProjection,
  targetProjection,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));
    const uniquePosition = getSingleUniquePosition(sourceProjection);
    const matchRequestBody = {
      module: 'opacities',
      action: 'match',
      lesion: uniquePosition,
      match_lesion: [targetProjection[1], targetProjection[2]],
      study_id: studyId,
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
    };

    const response = await fetchPost('diagnostic_revision_step', matchRequestBody, 'bbox');

    if (response.success) {
      dispatch(actions.setDraggingLesionId(null));
      dispatch(actions.setCustomOpacities(response.data.customOpacities));
      dispatch(actions.setOpacitiesLocalChanges(true));
      dispatch(
        actions.setSnackbar({
          msg: 'diagnose.match_findings',
          severity: 'success',
        })
      );
      dispatch(actions.updateJustEditedRows(response?.updatedFindingID));
    } else {
      dispatch(
        actions.setSnackbar({
          msg: `${t('diagnose.general_error')} ${response.msg}`,
          severity: 'error',
        })
      );
      // Reset draggingLesionId after failed drag operation
      dispatch(actions.setDraggingLesionId(null));
    }
  } catch (error) {
    console.error('Error in handleMatch:', error);
    dispatch(
      actions.setSnackbar({
        msg: 'diagnose.general_error',
        severity: 'error',
      })
    );
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

//Change finding type (opacities <-> microcalc)
export const handleTypeChange = async (
  studyId,
  moduleType,
  lesionInfo,
  newType,
  customMicrocalc,
  customOpacities,
  dispatch,
  actions,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));

    const uniquePosition = getSingleUniquePosition(lesionInfo);

    const requestBody = {
      module: moduleType,
      action: 'class_type_change',
      lesion: uniquePosition,
      new_type: newType,
      study_id: studyId,
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
    };

    const response = await fetchPost('diagnostic_revision_step', requestBody, 'bbox');

    if (!response?.success) {
      return dispatchError(response?.msg, dispatch, actions, t);
    }

    const { data } = response;

    dispatchCustomLesionModulesUpdate(moduleType, data, dispatch, actions);
    dispatch(actions.updateJustEditedRows(response?.updatedFindingID));

    dispatch(
      actions.setSnackbar({
        msg: 'diagnose.type_changed',
        severity: 'success',
      })
    );
  } catch (error) {
    console.error('Error in handleTypeChange:', error);
    dispatchError(error?.message, dispatch, actions, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};
